/** Shared between ScanUploadModal and dentist assistant (STL + photos). */

/**
 * Liste plate des items d’un groupe (pour upload / catégorie, etc.).
 * @param {{ items?: array, sections?: array<{ items: array }> }} group
 */
export function getPhotoGroupItems(group) {
  if (group.sections?.length) {
    return group.sections.flatMap((s) => s.items);
  }
  return group.items || [];
}

/** Blocs UI (sections numérotées ou liste plate). */
export function getPhotoGroupSections(group) {
  if (group.sections?.length) return group.sections;
  return [{ label: null, title: null, items: group.items || [] }];
}

export const PHOTO_GROUPS = [
  {
    id: 'clinicalPhotos',
    title: 'Photos cliniques',
    color: 'bg-blue-500',
    sections: [
      {
        label: '',
        title: 'Sourire de face, Face neutre',
        items: [
          {
            key: 'smileFront',
            label: 'Sourire de face',
            guideSrc: '/photo-guides/smileFront.webp',
            guideAlt: 'Exemple illustratif : portrait de face avec sourire.',
          },
          {
            key: 'neutralFace',
            label: 'Face neutre',
            guideSrc: '/photo-guides/neutralFace.webp',
            guideAlt: 'Exemple illustratif : portrait de face expression neutre.',
          },
        ],
      },
      {
        label: '',
        title: 'Profil',
        items: [
          {
            key: 'profile',
            label: 'Profil',
            guideSrc: '/photo-guides/profile.webp',
            guideAlt: 'Exemple illustratif : photographie de profil strict.',
          },
        ],
      },
    ],
  },
  {
    id: 'intraoralPhotos',
    title: 'Photos intra-orales',
    color: 'bg-green-500',
    sections: [
      {
        label: '',
        title: 'Latérale gauche, Latérale droite',
        items: [
          {
            key: 'leftLateral',
            label: 'Latérale gauche',
            guideSrc: '/photo-guides/leftLateral.webp',
            guideAlt:
              'Exemple illustratif : vue intra-orale latérale gauche (arcades en occlusion).',
          },
          {
            key: 'rightLateral',
            label: 'Latérale droite',
            guideSrc: '/photo-guides/rightLateral.webp',
            guideAlt:
              'Exemple illustratif : vue intra-orale latérale droite (arcades en occlusion).',
          },
        ],
      },
      {
        label: '',
        title: 'Occlusion supérieure, Occlusion inférieure',
        items: [
          {
            key: 'occlusionSup',
            label: 'Occlusion supérieure',
            guideSrc: '/photo-guides/occlusionSup.webp',
            guideAlt: 'Exemple illustratif : vue occlusale de l’arcade supérieure.',
          },
          {
            key: 'occlusionInf',
            label: 'Occlusion inférieure',
            guideSrc: '/photo-guides/occlusionInf.webp',
            guideAlt: 'Exemple illustratif : vue occlusale de l’arcade inférieure.',
          },
        ],
      },
      {
        label: '',
        title: 'Vue de face (morsure)',
        items: [
          {
            key: 'frontView',
            label: 'Vue de face (morsure)',
            guideSrc: '/photo-guides/frontView.webp',
            guideAlt: 'Exemple illustratif : vue frontale de la morsure.',
          },
        ],
      },
    ],
  },
  {
    id: 'xRays',
    title: 'Radiographies',
    color: 'bg-purple-500',
    items: [
      {
        key: 'panoramicXray',
        label: 'Rayons X panoramique',
        guideSrc: '/photo-guides/panoramicXray.webp',
        guideAlt: 'Exemple illustratif : radiographie panoramique.',
      },
      {
        key: 'tlrProfile',
        label: 'TLR profil',
        guideSrc: '/photo-guides/tlrProfile.webp',
        guideAlt: 'Exemple illustratif : téléradiographie latérale de profil (TLR).',
      },
    ],
  },
];

export const DOCUMENT_TYPES = [
  { value: 'report', label: 'Rapport' },
  { value: 'prescription', label: 'Ordonnance' },
  { value: 'consent', label: 'Consentement' },
  { value: 'xray', label: 'Radio' },
  { value: 'other', label: 'Autre' },
];

/** Libellé UI pour une clé photo (smileFront, …). */
export function getPhotoLabelByKey(photoKey) {
  if (!photoKey) return null;
  for (const group of PHOTO_GROUPS) {
    const item = getPhotoGroupItems(group).find((i) => i.key === photoKey);
    if (item?.label) return item.label;
  }
  return null;
}

/** Les 10 emplacements photo obligatoires avant envoi de la conception. */
export function getRequiredClinicalPhotoKeys() {
  return PHOTO_GROUPS.flatMap((group) => getPhotoGroupItems(group).map((item) => item.key));
}

/**
 * @param {Record<string, unknown>} uploadedByKey — clés photo → enregistrement ou truthy
 */
export function getMissingClinicalPhotoKeys(uploadedByKey = {}) {
  return getRequiredClinicalPhotoKeys().filter((key) => !uploadedByKey[key]);
}

export function hasAllRequiredClinicalPhotos(uploadedByKey = {}) {
  return getMissingClinicalPhotoKeys(uploadedByKey).length === 0;
}
